import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
} from "react-bootstrap";
import Calendar from "react-calendar";
import AsyncSelect from "react-select/async";
import "react-calendar/dist/Calendar.css";
import "./Feriados.css";
import { FaCalendarAlt, FaPen, FaSave, FaTimes, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import api from "../../services/api";
import { hasPerm } from "../../common/utils";
import Paginacao from "../../common/pagination";

const toIsoDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatDateBr = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
};

const getTodayNoTime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

const getTodayIso = () => toIsoDate(getTodayNoTime());

const defaultFormState = {
    descricao: "",
    escopo: "global",
    recorrente_anual: false,
    status: true,
};

const normalizePaginationData = (data) => {
    if (Array.isArray(data)) {
        return { count: data.length, next: null, previous: null, results: data };
    }

    return data || { count: 0, next: null, previous: null, results: [] };
};

export default function Feriados() {
    const [listRegistros, setListRegistros] = useState({ count: 0, next: null, previous: null, results: [] });
    const [registrosCalendario, setRegistrosCalendario] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [pageNumb, setPageNumb] = useState(1);

    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(getTodayIso());
    const [formData, setFormData] = useState(defaultFormState);
    const [selectedProgramas, setSelectedProgramas] = useState([]);
    const [programaBuscaSelecionado, setProgramaBuscaSelecionado] = useState(null);

    const blockedDatesMap = useMemo(() => {
        const map = {};
        registrosCalendario.filter((item) => item.status).forEach((item) => {
            if (!map[item.data]) {
                map[item.data] = [];
            }
            map[item.data].push(item);
        });
        return map;
    }, [registrosCalendario]);

    const blockedDatesSet = useMemo(() => {
        return new Set(Object.keys(blockedDatesMap));
    }, [blockedDatesMap]);

    const carregarFeriadosCalendario = async () => {
        try {
            const response = await api.get("/feriados/?limit=500");
            const registros = Array.isArray(response.data) ? response.data : (response.data?.results || []);
            setRegistrosCalendario(registros);
        } catch {
            setRegistrosCalendario([]);
        }
    };

    const carregarFeriados = async (page = 1) => {
        setLoading(true);
        setPageNumb(page);
        setError("");

        try {
            const response = await api.get(`/feriados/?page=${page}`);
            setListRegistros(normalizePaginationData(response.data));
        } catch (err) {
            if (err.response && err.response.status === 403) {
                setError("Você não tem permissão para acessar essa área.");
            } else {
                setError("Erro ao consultar feriados. Tente novamente.");
            }
            setListRegistros({ count: 0, next: null, previous: null, results: [] });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregarFeriadosCalendario();
        carregarFeriados(1);
    }, []);

    const onClickPagina = (numPage) => {
        carregarFeriados(numPage);
    };

    const openCreateModal = (dateIso) => {
        setEditingId(null);
        setSelectedDate(dateIso);
        setFormData(defaultFormState);
        setSelectedProgramas([]);
        setProgramaBuscaSelecionado(null);
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditingId(item.id);
        setSelectedDate(item.data);
        setFormData({
            descricao: item.descricao || "",
            escopo: item.escopo || "global",
            recorrente_anual: !!item.recorrente_anual,
            status: !!item.status,
        });
        setSelectedProgramas((item.programas_dados || []).map((programa) => ({
            value: programa.id,
            label: programa.nome,
        })));
        setProgramaBuscaSelecionado(null);
        setShowModal(true);
    };

    const handleCalendarClick = (date) => {
        openCreateModal(toIsoDate(date));
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: "Tem certeza?",
            text: "Essa ação não poderá ser desfeita.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar",
        }).then(async (result) => {
            if (!result.isConfirmed) return;

            try {
                await api.delete(`/feriados/${id}/`);
                await carregarFeriadosCalendario();
                await carregarFeriados(pageNumb);
                Swal.fire("Excluído", "Bloqueio removido com sucesso.", "success");
            } catch {
                Swal.fire("Erro", "Não foi possível remover o bloqueio.", "error");
            }
        });
    };

    const buscarProgramas = async (inputValue) => {
        try {
            const response = await api.get(`/programas/?nome=${inputValue || ""}&limit=20`);
            const registros = Array.isArray(response.data) ? response.data : (response.data?.results || []);

            return registros
                .filter((item) => item && item.id)
                .map((item) => ({
                    value: item.id,
                    label: item.nome || `Programa ${item.id}`,
                }));
        } catch {
            return [];
        }
    };

    const handleProgramaSelect = (selectedOption) => {
        setProgramaBuscaSelecionado(null);
        if (!selectedOption) return;

        const exists = selectedProgramas.some((programa) => programa.value === selectedOption.value);
        if (exists) return;

        setSelectedProgramas((prev) => [...prev, selectedOption]);
    };

    const removerProgramaSelecionado = (programaId) => {
        setSelectedProgramas((prev) => prev.filter((programa) => programa.value !== programaId));
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!selectedDate) {
            Swal.fire("Data obrigatória", "Selecione a data do bloqueio no calendário.", "warning");
            return;
        }

        if (!formData.descricao || !formData.descricao.trim()) {
            Swal.fire("Motivo obrigatório", "Informe o motivo do bloqueio.", "warning");
            return;
        }

        if (formData.escopo === "programa" && selectedProgramas.length === 0) {
            Swal.fire("Programa obrigatório", "Selecione ao menos um programa para bloqueio específico.", "warning");
            return;
        }

        const payload = {
            descricao: formData.descricao.trim(),
            data: selectedDate,
            escopo: formData.escopo,
            programas: formData.escopo === "programa" ? selectedProgramas.map((programa) => programa.value) : [],
            recorrente_anual: formData.recorrente_anual,
            status: formData.status,
        };

        setSaving(true);

        try {
            if (editingId) {
                await api.put(`/feriados/${editingId}/`, payload);
            } else {
                await api.post(`/feriados/`, payload);
            }

            await carregarFeriadosCalendario();
            await carregarFeriados(pageNumb);
            setShowModal(false);
            setEditingId(null);
            setFormData(defaultFormState);
            setSelectedProgramas([]);
            setProgramaBuscaSelecionado(null);

            Swal.fire("Sucesso", editingId ? "Bloqueio atualizado com sucesso." : "Bloqueio cadastrado com sucesso.", "success");
        } catch (err) {
            let erroMsg = "Não foi possível salvar o bloqueio.";
            if (err.response && err.response.data) {
                const dataErro = err.response.data;
                if (typeof dataErro === "string") {
                    erroMsg = dataErro;
                } else {
                    const firstKey = Object.keys(dataErro)[0];
                    if (firstKey && dataErro[firstKey]) {
                        erroMsg = Array.isArray(dataErro[firstKey]) ? dataErro[firstKey][0] : dataErro[firstKey];
                    }
                }
            }
            Swal.fire("Erro", erroMsg, "error");
        } finally {
            setSaving(false);
        }
    };

    if (!hasPerm("view_feriado")) {
        return <Navigate to="/sem-permissao" />;
    }

    return (
        <div>
            <Row className="align-items-center mb-3">
                <Col sm={12} lg={12}>
                    <h4 className="page-title mb-0">Feriados e Bloqueios de Datas</h4>
                    <small className="text-white">Clique no calendário para bloquear uma data e informar o motivo.</small>
                </Col>
            </Row>

            {error && (
                <Alert variant="warning" className="mb-3">
                    {error}
                </Alert>
            )}

            <Row>
                <Col md={4} className="mb-4">
                    <Card className="cardDestaque h-100">
                        <Card.Header className="bg-white fw-bold">
                            <FaCalendarAlt className="me-2" /> Calendário
                        </Card.Header>
                        <Card.Body className="feriados-calendar-wrap">
                            <Calendar
                                locale="pt-BR"
                                onClickDay={handleCalendarClick}
                                tileClassName={({ date, view }) => {
                                    if (view !== "month") return null;
                                    return blockedDatesSet.has(toIsoDate(date)) ? "feriado-tile-blocked" : null;
                                }}
                            />

                            <div className="mt-3 d-flex align-items-center gap-2">
                                <Badge bg="danger">Bloqueado</Badge>
                                <small className="text-muted">Datas em vermelho possuem bloqueio ativo.</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={8} className="mb-4">
                    <Card className="cardDestaque h-100">
                        <Card.Header className="bg-white fw-bold d-flex justify-content-between align-items-center">
                            <span>Bloqueios Registrados</span>
                            <Badge bg="secondary">{listRegistros?.count || 0}</Badge>
                        </Card.Header>
                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" />
                                </div>
                            ) : (
                                <>
                                    <Table responsive className="custom-table align-middle">
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Motivo</th>
                                                <th>Escopo</th>
                                                <th>Programas</th>
                                                <th>Recorrente</th>
                                                <th>Status</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(listRegistros?.results || []).length === 0 ? (
                                                <tr>
                                                    <td colSpan={7} className="text-center">Nenhum bloqueio cadastrado.</td>
                                                </tr>
                                            ) : (
                                                (listRegistros.results || []).map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{formatDateBr(item.data)}</td>
                                                        <td>{item.descricao || "-"}</td>
                                                        <td>
                                                            {item.escopo === "global" ? (
                                                                <Badge bg="dark">Todos</Badge>
                                                            ) : (
                                                                <Badge bg="info">Específico</Badge>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {item.escopo === "global"
                                                                ? "Todos os programas"
                                                                : ((item.programas_dados || []).map((p) => p.nome).join(", ") || "-")
                                                            }
                                                        </td>
                                                        <td>{item.recorrente_anual ? "Sim" : "Não"}</td>
                                                        <td>{item.status ? "Ativo" : "Inativo"}</td>
                                                        <td className="text-end">
                                                            <Button variant="light" size="sm" className="me-2" onClick={() => openEditModal(item)}>
                                                                <FaPen />
                                                            </Button>
                                                            <Button variant="danger" size="sm" onClick={() => handleDelete(item.id)}>
                                                                <FaTrash />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>

                                    <Paginacao
                                        dados={listRegistros}
                                        loading={loading}
                                        page_numb={pageNumb}
                                        _onClickPagina={onClickPagina}
                                    />
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Form onSubmit={handleSave}>
                    <Modal.Header closeButton>
                        <Modal.Title>{editingId ? "Editar Bloqueio" : "Novo Bloqueio"} - {formatDateBr(selectedDate)}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Motivo</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.descricao}
                                onChange={(e) => setFormData((prev) => ({ ...prev, descricao: e.target.value }))}
                                placeholder="Ex: Feriado local"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Escopo do bloqueio</Form.Label>
                            <Form.Control
                                as="select"
                                value={formData.escopo}
                                onChange={(e) => {
                                    setFormData((prev) => ({ ...prev, escopo: e.target.value }));
                                    setSelectedProgramas([]);
                                    setProgramaBuscaSelecionado(null);
                                }}
                            >
                                <option value="global">Todos os programas</option>
                                <option value="programa">Programas específicos</option>
                            </Form.Control>
                        </Form.Group>

                        {formData.escopo === "programa" && (
                            <Form.Group className="mb-3">
                                <Form.Label>Buscar programas</Form.Label>
                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={buscarProgramas}
                                    value={programaBuscaSelecionado}
                                    onChange={handleProgramaSelect}
                                    placeholder="Digite para buscar e adicionar"
                                    noOptionsMessage={() => "Nenhum programa encontrado"}
                                    loadingMessage={() => "Buscando programas..."}
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />

                                <div className="mt-3 d-flex flex-wrap gap-2">
                                    {selectedProgramas.length === 0 ? (
                                        <small className="text-muted">Nenhum programa selecionado.</small>
                                    ) : (
                                        selectedProgramas.map((programa) => (
                                            <Badge key={programa.value} bg="info" className="feriado-programa-badge">
                                                {programa.label}
                                                <button
                                                    type="button"
                                                    className="badge-programa-remove"
                                                    onClick={() => removerProgramaSelecionado(programa.value)}
                                                    aria-label={`Remover ${programa.label}`}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </Badge>
                                        ))
                                    )}
                                </div>
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="switch-recorrente-anual"
                                label="Bloquear todo ano (recorrente)"
                                checked={formData.recorrente_anual}
                                onChange={(e) => setFormData((prev) => ({ ...prev, recorrente_anual: e.target.checked }))}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Check
                                type="switch"
                                id="switch-status-bloqueio"
                                label="Bloqueio ativo"
                                checked={formData.status}
                                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.checked }))}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" onClick={() => setShowModal(false)} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button variant="success" type="submit" disabled={saving}>
                            {saving ? (
                                <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                                <>
                                    <FaSave className="me-1" /> Salvar
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}
